<template>
    <div class="html-editor-wrap">
        <pre
            ref="styledHtml"
            class="html-editor-pre"
            :style="{ maxHeight: maxHeight + 'px' }"
            contenteditable="true"
            @input="onHtmlInput"
            @keydown.tab.prevent="insertTab"
            @keydown.enter.prevent="autoIndent"
            @paste="onPasteIntoHtml"
            @blur="onHtmlBlur"
            @focus="onHtmlFocus"
            @click="onHtmlClick"
            @keyup="onKeyUp">
        </pre>

        <div class="html-editor-toolbar" v-if="showToolbar">
            <button type="button" class="btn btn-xxs" @mousedown.prevent.stop @click="clearFormatting">
                Clear formatting
            </button>
            <slot name="toolbar"></slot>
        </div>
    </div>
</template>

<script>
export default {
    name: `HtmlEditor`,
    props: {
        value: {
            type: String,
            default: ``
        },
        maxHeight: {
            type: Number,
            default: 600
        },
        showToolbar: {
            type: Boolean,
            default: true
        },
        placeholder: {
            type: String,
            default: ``
        }
    },

    data() {
        return {
            highlightDebounce: null,
            localValue: ``,
            isTyping: false,
            typingTimeout: null,
            initialFocusHighlight: false
        };
    },

    mounted() {
        this.localValue = this.value || ``;
        this.$nextTick(() => {
            if (document.activeElement !== this.$refs.styledHtml) {
                this.renderHighlightedToHtml(false);
            }
        });
    },

    beforeDestroy() {
        if (this.highlightDebounce) clearTimeout(this.highlightDebounce);
        if (this.typingTimeout) clearTimeout(this.typingTimeout);
    },

    methods: {
        onHtmlInput() {
            const el = this.$refs.styledHtml;
            if (!el) return;

            const raw = this.getPlainFromHtml(el);

            if (raw !== this.localValue) {
                this.localValue = raw;
                this.$emit(`input`, raw);

                this.isTyping = true;

                if (this.highlightDebounce) {
                    clearTimeout(this.highlightDebounce);
                }
                if (this.typingTimeout) {
                    clearTimeout(this.typingTimeout);
                }

                this.typingTimeout = setTimeout(() => {
                    this.isTyping = false;
                }, 200);

                const delay = this.initialFocusHighlight ? 2000 : 1000;

                this.highlightDebounce = setTimeout(() => {
                    if (document.activeElement === el && !this.isTyping) {
                        const selection = this.saveSelection(el);
                        this.renderHighlightedToHtml(true, selection);
                    }
                }, delay);
            }
        },
        onHtmlBlur() {
            this.$emit(`blur`, this.localValue);

            if (this.highlightDebounce) {
                clearTimeout(this.highlightDebounce);
                this.highlightDebounce = null;
            }

            setTimeout(() => {
                if (document.activeElement !== this.$refs.styledHtml) {
                    this.renderHighlightedToHtml(false);
                }
            }, 100);
        },
        onHtmlFocus() {
            this.$emit(`focus`, this.localValue);

            this.initialFocusHighlight = true;

            setTimeout(() => {
                this.initialFocusHighlight = false;
            }, 100);
        },
        onHtmlClick() {
            if (this.highlightDebounce) {
                clearTimeout(this.highlightDebounce);
                this.highlightDebounce = null;
            }
        },
        onKeyUp(e) {
            const navigationKeys = [`ArrowUp`, `ArrowDown`, `ArrowLeft`, `ArrowRight`, `Home`, `End`, `PageUp`, `PageDown`];
            if (navigationKeys.includes(e.key)) {
                // TODO: Implement navigation
                return;
            }
        },
        insertTab() {
            const el = this.$refs.styledHtml;
            const sel = window.getSelection();
            if (!el || !sel || sel.rangeCount === 0) return;

            try {
                const range = sel.getRangeAt(0);
                const selectedText = range.toString();

                if (selectedText.includes(`\n`)) {
                    // Multi-line selection, indent each line
                    const lines = selectedText.split(`\n`);
                    const indentedText = lines.map(line => `  ` + line).join(`\n`);

                    range.deleteContents();
                    range.insertNode(document.createTextNode(indentedText));

                    // Move cursor after the inserted text
                    range.collapse(false);
                    sel.removeAllRanges();
                    sel.addRange(range);
                } else {
                    // Single line or no selection: insert tab
                    range.deleteContents();
                    const tabNode = document.createTextNode(`  `); // Use spaces instead of tab
                    range.insertNode(tabNode);

                    // Move caret to end of inserted text
                    range.setStart(tabNode, tabNode.nodeValue.length);
                    range.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(range);
                }

                // Update the component data without triggering highlight
                const raw = this.getPlainFromHtml(el);
                this.localValue = raw;
                this.$emit(`input`, raw);

                // Trigger delayed highlighting with longer delay to avoid focus issues
                if (this.highlightDebounce) {
                    clearTimeout(this.highlightDebounce);
                }
                this.highlightDebounce = setTimeout(() => {
                    if (document.activeElement === el) {
                        const selection = this.saveSelection(el);
                        this.renderHighlightedToHtml(true, selection);
                    }
                }, 1500);

            } catch (error) {
                console.warn(`Failed to insert tab:`, error);
            }
        },

        autoIndent() {
            const el = this.$refs.styledHtml;
            if (!el) return;

            try {
                const sel = window.getSelection();
                if (!sel || sel.rangeCount === 0) return;

                const range = sel.getRangeAt(0);
                const {start} = this.saveSelection(el);
                const txt = this.getPlainFromHtml(el);
                const lineStart = txt.lastIndexOf(`\n`, Math.max(0, start - 1)) + 1;
                const currentIndent = (/^\s*/.exec(txt.slice(lineStart, start)) || [``])[0];

                range.deleteContents();
                const indentNode = document.createTextNode(`\n${currentIndent}`);
                range.insertNode(indentNode);

                // Move caret to end of inserted text
                range.setStart(indentNode, indentNode.nodeValue.length);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);

                // Update the component data without triggering immediate highlight
                const raw = this.getPlainFromHtml(el);
                this.localValue = raw;
                this.$emit(`input`, raw);

                // Trigger delayed highlighting with longer delay to avoid focus issues
                if (this.highlightDebounce) {
                    clearTimeout(this.highlightDebounce);
                }
                this.highlightDebounce = setTimeout(() => {
                    if (document.activeElement === el) {
                        const selection = this.saveSelection(el);
                        this.renderHighlightedToHtml(true, selection);
                    }
                }, 1500);

            } catch (error) {
                console.warn(`Failed to auto-indent:`, error);
            }
        },
        onPasteIntoHtml(e) {
            e.preventDefault();

            const html = (e.clipboardData && e.clipboardData.getData(`text/html`)) || ``;
            const text = (e.clipboardData && e.clipboardData.getData(`text/plain`)) || ``;

            if (!html && !text) return;

            const el = this.$refs.styledHtml;
            const sel = window.getSelection();
            if (!el || !sel || sel.rangeCount === 0) return;

            try {
                const range = sel.getRangeAt(0);
                range.deleteContents();

                // Prefer plain text to avoid formatting issues
                const contentToInsert = text || html;
                const textNode = document.createTextNode(contentToInsert);
                range.insertNode(textNode);

                // Move caret to end of inserted text
                range.setStart(textNode, textNode.nodeValue.length);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);

                // Update the component data
                const raw = this.getPlainFromHtml(el);
                this.localValue = raw;
                this.$emit(`input`, raw);

                // Trigger delayed highlighting with longer delay to avoid focus issues
                if (this.highlightDebounce) {
                    clearTimeout(this.highlightDebounce);
                }
                this.highlightDebounce = setTimeout(() => {
                    if (document.activeElement === el) {
                        const selection = this.saveSelection(el);
                        this.renderHighlightedToHtml(true, selection);
                    }
                }, 1500);

            } catch (error) {
                console.warn(`Failed to paste into HTML:`, error);
            }
        },
        renderHighlightedToHtml(preserve = true, savedSelection = null) {
            const el = this.$refs.styledHtml;
            if (!el) return;

            const currentContent = this.localValue || ``;
            const highlightedContent = this.highlightHTML(this.prettyPrintHtml(currentContent));

            // Check if content actually needs updating
            const currentHTML = el.innerHTML;
            if (currentHTML === highlightedContent) {
                return;
            }

            // Check if element was focused before update
            const wasFocused = document.activeElement === el;

            // Save selection if we need to preserve it
            const sel = savedSelection || (preserve ? this.saveSelection(el) : null);

            // Update the content
            el.innerHTML = highlightedContent;

            // Restore cursor position if requested and valid
            if (preserve && sel && sel.valid) {
                this.restoreSelection(el, sel, wasFocused);
            } else if (wasFocused) {
                // Even without selection, restore focus if element was previously focused
                this.$nextTick(() => {
                    el.focus();
                });
            }
        },
        getPlainFromHtml(el) {
            return (el.textContent || ``).replace(/\r\n/g, `\n`);
        },
        insertText(text) {
            const el = this.$refs.styledHtml;
            const sel = window.getSelection();
            if (!el || !sel || sel.rangeCount === 0) return;

            try {
                const range = sel.getRangeAt(0);
                range.deleteContents();
                const node = document.createTextNode(text);
                range.insertNode(node);

                // Move caret to end of inserted text
                range.setStart(node, node.nodeValue.length);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);

                // Update the value
                const raw = this.getPlainFromHtml(el);
                this.localValue = raw;
                this.$emit(`input`, raw);
            } catch (error) {
                console.warn(`Failed to insert text:`, error);
            }
        },
        saveSelection(root) {
            try {
                const sel = window.getSelection && window.getSelection();
                if (!sel || sel.rangeCount === 0 || !root.contains(sel.anchorNode)) {
                    return {start: 0, end: 0, valid: false};
                }

                const range = sel.getRangeAt(0);

                // Calculate start position relative to text content
                const preStart = range.cloneRange();
                preStart.selectNodeContents(root);
                preStart.setEnd(range.startContainer, range.startOffset);
                const start = preStart.toString().length;

                // Calculate end position relative to text content
                const preEnd = range.cloneRange();
                preEnd.selectNodeContents(root);
                preEnd.setEnd(range.endContainer, range.endOffset);
                const end = preEnd.toString().length;

                return {start, end, valid: true};
            } catch (error) {
                console.warn(`Failed to save selection:`, error);
                return {start: 0, end: 0, valid: false};
            }
        },
        restoreSelection(root, {start = 0, end = 0, valid = true} = {}, shouldFocus = false) {
            if (!root || !valid) return;

            try {
                // Wait for the DOM to settle before restoring selection
                this.$nextTick(() => {
                    // Restore focus first if needed
                    if (shouldFocus) {
                        root.focus();
                    }

                    const walker = document.createTreeWalker(
                        root,
                        NodeFilter.SHOW_TEXT,
                        null
                    );

                    let range = document.createRange();
                    let pos = 0;
                    let startNode = null;
                    let startOffset = 0;
                    let endNode = null;
                    let endOffset = 0;
                    let node;

                    while ((node = walker.nextNode())) {
                        const nodeLength = node.nodeValue.length;
                        const nextPos = pos + nodeLength;

                        if (!startNode && start <= nextPos) {
                            startNode = node;
                            startOffset = Math.max(0, Math.min(start - pos, nodeLength));
                        }

                        if (end <= nextPos) {
                            endNode = node;
                            endOffset = Math.max(0, Math.min(end - pos, nodeLength));
                            break;
                        }

                        pos = nextPos;
                    }

                    // Fallback to root if no text nodes found
                    if (!startNode) {
                        startNode = root;
                        startOffset = 0;
                    }

                    if (!endNode) {
                        endNode = startNode;
                        endOffset = startOffset;
                    }

                    range.setStart(startNode, startOffset);
                    range.setEnd(endNode, endOffset);

                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);

                    // Ensure the cursor is visible
                    if (startNode && startNode.parentElement) {
                        startNode.parentElement.scrollIntoView({ block: `nearest`, behavior: `instant` });
                    }
                });
            } catch (error) {
                console.warn(`Failed to restore selection:`, error);
                // Fallback: place cursor at the end and restore focus if needed
                this.$nextTick(() => {
                    try {
                        if (shouldFocus) {
                            root.focus();
                        }
                        const sel = window.getSelection();
                        sel.removeAllRanges();
                        const range = document.createRange();
                        range.selectNodeContents(root);
                        range.collapse(false);
                        sel.addRange(range);
                    } catch (e) {
                        console.error(e)
                    }
                });
            }
        },
        clearFormatting() {
            const src = this.localValue || ``;
            const tmp = document.createElement(`div`);
            tmp.innerHTML = src;
            const plain = (tmp.textContent || tmp.innerText || ``).replace(/\r\n/g, `\n`);

            this.localValue = plain;
            this.$emit(`input`, plain);
            this.$emit(`blur`, plain);

            this.$nextTick(() => {
                this.renderHighlightedToHtml(false);
            });
        },
        prettyPrintHtml(src) {
            const s = (src || ``).replace(/\r\n/g, `\n`).replace(/\t/g, `  `).trim();
            if (!s) return ``;
            const out = [];
            const voids = new Set([`area`, `base`, `br`, `col`, `embed`, `hr`, `img`, `input`, `link`, `meta`, `param`, `source`, `track`, `wbr`]);
            let indent = 0;
            const pushLine = (line) => out.push(`  `.repeat(Math.max(indent, 0)) + line);

            const parts = s.split(/(<[^>]+>)/g).filter(Boolean);
            for (let i = 0; i < parts.length; i++) {
                const t = parts[i];
                if (t[0] === `<`) {
                    const isClose = /^<\//.test(t);
                    const isComment = /^<!--/.test(t);
                    const tagName = (t.match(/^<\/?\s*([a-z0-9:-]+)/i) || [``, ``])[1].toLowerCase();
                    const isVoid = voids.has(tagName) || /\/>$/.test(t);
                    if (isClose) indent--;
                    if (isComment) {
                        pushLine(t.trim());
                        continue;
                    }
                    pushLine(t.trim());
                    if (!isClose && !isVoid) indent++;
                } else {
                    const text = t.replace(/\s+/g, ` `).trim();
                    if (text) pushLine(text);
                }
            }
            return out.join(`\n`);
        },
        highlightHTML(src) {
            if (!src) return ``;
            const esc = this.escapeHtml(src);
            let h = esc.replace(/(&lt;!--[\s\S]*?--&gt;)/g, `<span class="tok comment">$1</span>`);
            h = h.replace(
                /(&lt;\/?)([a-zA-Z0-9:-]+)((?:\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))*)(\s*\/?&gt;)/g,
                (m, open, name, attrs, close) => {
                    const attrsColored = attrs.replace(
                        /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(\s*=\s*)(".*?"|'.*?'|[^\s"'=<>`]+)/g,
                        `<span class="tok attr">$1</span>$2<span class="tok string">$3</span>`
                    );
                    return `<span class="tok punct">${open}</span><span class="tok tag">${name}</span>${attrsColored}<span class="tok punct">${close}</span>`;
                }
            );
            h = h.replace(/(&amp;[a-zA-Z0-9#]+;)/g, `<span class="tok entity">$1</span>`);
            return h;
        },
        escapeHtml(s) {
            return (s || ``)
                .replace(/&/g, `&amp;`)
                .replace(/</g, `&lt;`)
                .replace(/>/g, `&gt;`);
        },
        focus() {
            if (this.$refs.styledHtml) {
                this.$refs.styledHtml.focus();
            }
        },
        setValue(value) {
            this.localValue = value || ``;
            this.renderHighlightedToHtml(false);
        }
    },
    watch: {
        value(newValue) {
            if (newValue !== this.localValue) {
                this.localValue = newValue || ``;
                this.renderHighlightedToHtml(false);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.html-editor-wrap {
    --toolbar-h: 32px;
    position: relative;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
    font-size: 12px;
    line-height: 1.5;
    border: 1px solid rgba(29, 78, 216, .65);
    border-radius: 6px;
    background: #1e1f22;
}

.html-editor-pre {
    position: relative;
    z-index: 1;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    tab-size: 4;
    padding: 8px;
    color: #cbd5e1;
    text-align: left;
    cursor: text;
    margin: 0;
    border: none;
    background: transparent;
    outline: none;

    :deep(.tok.punct) {
        color: #ffdb8f;
    }

    :deep(.tok.tag) {
        color: #ffdb8f;
    }

    :deep(.tok.attr) {
        color: #38a6f4;
    }

    :deep(.tok.string) {
        color: #fca5a5;
    }

    :deep(.tok.comment) {
        color: #94a3b8;
        font-style: italic;
    }

    :deep(.tok.entity) {
        color: #f9a8d4;
    }
}

.html-editor-toolbar {
    z-index: 2;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 4px 8px;
    background: rgba(2, 6, 23, .85);
    display: flex;
    align-items: center;
    gap: 6px;
    border-top: 1px solid rgba(99, 102, 241, .2);

    .btn {
        font-size: 11px;
        padding: 2px 6px;
        background: #172554;
        color: #c7d2fe;
        border: 1px solid #1d4ed8;
        border-radius: 4px;
        cursor: pointer;

        &:hover {
            background: #1e3a8a;
        }
    }
}
</style>
